import { CONST } from '@common/constants/const.constant';
import { useField } from '@common/hooks/use-field.hook';
import { useTimeout } from '@common/hooks/use-timeout.hook';
import React, { useCallback, useEffect, useRef } from 'react';
import { InputNumber } from './input-number.component';
import { IInputChangeHandler, IInputProps, Input } from './input.component';

type IFormInputProps<TFormValues> = Omit<IInputProps<string, keyof TFormValues>, 'value' | 'defaultValue'>;

export function FormInput<TFormValues>(props: IFormInputProps<TFormValues>): JSX.Element {
    const { onChange, onBlur, getCustomErrorMessage } = props;
    const { setFieldValue, setFieldTouched, value, triggerValidate, errorMessage } = useField<string>({
        ...props,
        getCustomErrorMessage,
    });
    const refValue = useRef(value);
    useEffect(() => {
        refValue.current = value;
    }, [value]);

    const { start: updateValueWithDelay } = useTimeout(
        useCallback(
            (newValue: string) => {
                refValue.current = newValue;
                setFieldValue(newValue);
                triggerValidate();
            },
            [setFieldValue, triggerValidate],
        ),
        CONST.DEBOUNCE,
    );

    const handleChange = useCallback<IInputChangeHandler>(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            refValue.current = e.target.value;
            onChange && onChange(e);
            updateValueWithDelay(e.target.value);
        },
        [onChange, updateValueWithDelay],
    );

    const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
            onBlur && onBlur(e);

            setFieldValue(refValue.current);
            setFieldTouched();
        },
        [onBlur, setFieldTouched, setFieldValue],
    );

    const handlePaste = useCallback(
        (e: React.ClipboardEvent) => {
            const val = e.clipboardData.getData('Text');

            refValue.current = val;
            setFieldValue(val);
            setFieldTouched();

            e.preventDefault();
        },
        [setFieldTouched, setFieldValue],
    );

    const newProps: IInputProps = {
        errorMessage,
        ...props,
        onChange: handleChange,
        onBlur: handleBlur,
        onPaste: handlePaste,
        name: props.name as string,
        value,
    };

    return props.type === 'number' ? <InputNumber {...newProps} /> : <Input {...newProps} />;
}
